import { endpoints } from '@/api/endpoints';
import {
  acceptOfferResponseSchema,
  bankIdOrderResponseSchema,
  createApplicationResponseSchema,
  createOpenBankingUrlResponseSchema,
  getApplicationResponseSchema,
  startApplicationAssessmentResponseSchema,
} from '@/api/SE/credit/schemas';
import type {
  CreateApplicationPayload,
  DeclineOfferOptions,
  StartApplicationAssessmentPayload,
} from '@/api/SE/credit/types';
import { apiRequest } from '@/api/utils/api';
import type { AddonProducts } from '@/constants/AddonProduct';
import type { UtmTagData } from '@/utils/tracking/tracking';

export const createApplication = (payload: CreateApplicationPayload) =>
  apiRequest({
    url: '/se/credit/applications',
    method: 'POST',
    schema: createApplicationResponseSchema,
    data: payload,
  });

export const startApplicationAssessment = ({
  applicationId,
  assessmentType,
}: StartApplicationAssessmentPayload) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}`,
    method: 'POST',
    schema: startApplicationAssessmentResponseSchema,
    data: { assessmentType },
  });

type SignOfferOptions = {
  applicationId: string;
  addonProducts?: AddonProducts[];
  utm?: UtmTagData;
};

export const signOffer = ({ applicationId, ...payload }: SignOfferOptions) =>
  apiRequest({
    url: endpoints.SE.credit.applications.applicationId(applicationId).signOffer,
    method: 'POST',
    schema: bankIdOrderResponseSchema,
    data: payload,
  });

export const initiateIncomeVerification = (applicationId: string) =>
  apiRequest({
    url: endpoints.SE.credit.applications.applicationId(applicationId).incomeVerification,
    method: 'POST',
    schema: bankIdOrderResponseSchema,
  });

export const acceptOffer = (applicationId: string) =>
  apiRequest({
    url: endpoints.SE.credit.applications.applicationId(applicationId).acceptOffer,
    method: 'POST',
    schema: acceptOfferResponseSchema,
  });

export const declineOffer = ({ applicationId, customerFeedback }: DeclineOfferOptions) =>
  apiRequest<void>({
    url: endpoints.SE.credit.applications.applicationId(applicationId).declineOffer,
    method: 'POST',
    data: { customerFeedback },
  });

export const getApplication = (applicationId: string) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}`,
    method: 'GET',
    schema: getApplicationResponseSchema,
  });

type CreateOpenBankingUrlOptions = {
  applicationId: string;
  redirectUrl: string;
};

export const createOpenBankingUrl = ({ applicationId, redirectUrl }: CreateOpenBankingUrlOptions) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}/open-banking-url`,
    method: 'POST',
    schema: createOpenBankingUrlResponseSchema,
    data: { redirectUrl },
  });
